import { apiUrl } from "../utils/api";
import { invalidateByPrefix } from "../utils/swrCache";
import type { NoteDto, NoteRefDto } from "./vorgangService";
import type { ReihenfolgePatch } from "../components/notizbloecke/blockModell";

/** Ein Eintrag des aggregierten Vorgang-Chats (Vorgang- + To-do-Notizen, SP10). */
export interface AggregatedNoteDto extends NoteDto {
  /** Herkunftstabelle — 'vorgang' = VorgangNote, 'todo' = TodoNote. */
  source: "vorgang" | "todo";
  /**
   * Gehört zur LAUFENDEN NOTIZ des Vorgangs (User 01.11.2026): ein Vorgang mit
   * To-dos hat oben einen eigenen Notizbereich ohne Verlauf. Fehlt = nein.
   */
  laufend?: boolean;
  /**
   * Herkunft eines Blocks, der nicht in KMVS geschrieben wird: 'remarkable' =
   * Handschrift vom reMarkable, im Notizfeld gesperrt. Fehlt = gewöhnliche Notiz.
   */
  quelle?: "remarkable";
  /** Bezogenes To-do (Besitzer bei 'todo', #-Verweis bei 'vorgang'); null = keins. */
  todoId: number | null;
  /** Betreff des bezogenen To-dos (Badge im Chat); null wenn kein Bezug. */
  todoTitel: string | null;
}

async function req<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`API error ${res.status}: ${txt}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

/** Vorgang-Notizen (Notiz-Chat mit Threading + @Erwähnung). Spiegelt noteService. */
export const vorgangNoteService = {
  async list(vorgangId: number): Promise<NoteDto[]> {
    return req<NoteDto[]>(apiUrl(`vorgang/${vorgangId}/notes`));
  },
  /** Aggregierter Chat: Vorgang- + To-do-Notizen des Vorgangs (SP10). */
  async listAggregated(vorgangId: number): Promise<AggregatedNoteDto[]> {
    return req<AggregatedNoteDto[]>(apiUrl(`vorgang/${vorgangId}/notes?includeTodos=1`));
  },
  /**
   * Der Chat der Vorgangsseite seit PO 30.08.2026: NUR die Vorgangs-Notizen.
   * Was an einem To-do hängt, steht beim To-do — im Vorgangs-Chat stand es
   * zwischen allen anderen und musste bei acht To-dos erst gesucht werden.
   *
   * Dasselbe View-Modell wie `listAggregated` (mit `source` und den
   * `#`-Badges), nur ohne die To-do-Notizen — deshalb `includeTodos=0` und
   * nicht der schlanke Endpoint ohne Parameter.
   */
  async listNurVorgang(vorgangId: number): Promise<AggregatedNoteDto[]> {
    return req<AggregatedNoteDto[]>(apiUrl(`vorgang/${vorgangId}/notes?includeTodos=0`));
  },
  async create(
    vorgangId: number,
    input: {
      text: string;
      mentions?: string[];
      parentId?: number | null;
      todoId?: number | null;
      /** In die laufende Notiz des Vorgangs schreiben (User 01.11.2026). */
      laufend?: boolean;
      riskObjectId?: number | null;
      contractId?: number | null;
      /**
       * Abweichender Zeitpunkt der Notiz — etwa eine Gesprächsnotiz, die am
       * Folgetag nachgetragen wird. Ohne Angabe zählt der Moment des Anlegens.
       * Das Backend deckelt den Bereich auf ±5 Jahre (`gewaehlterZeitpunkt`).
       */
      timestamp?: string | null;
      /**
       * Kommentar an einem Verlaufseintrag. Höchstens einer der vier — das
       * Backend leert die übrigen (`einVerlaufsBezug`).
       */
      mailLinkId?: number | null;
      callLogId?: number | null;
      documentId?: number | null;
      /** Kommentar an einem Termin (PO 20.09.2026). */
      terminId?: number | null;
      /** Kommentar an einem Mail-Entwurf (User 22.10.2026): dessen Graph-Id. */
      entwurfId?: string | null;
      /**
       * Die markierte Textstelle, an der der Kommentar hängt (PO 18.09.2026);
       * ohne sie gehört er zur ganzen Notiz. Derselbe Wert steht im HTML der
       * Elternnotiz (`data-anker`) — gleich heißt derselbe Faden.
       */
      anker?: string;
      /**
       * Blockfelder (PO 08.09.2026). `format: 'html'` kennzeichnet Inhalt aus
       * dem Blockeditor — ohne die Angabe rendert die Fläche das Markup beim
       * nächsten Laden als Klartext.
       */
      format?: "text" | "html";
      refs?: NoteRefDto[];
      sortIndex?: number | null;
      /** Gezogene Breite in Pixel; null = mit dem Inhalt wachsen. */
      breite?: number | null;
    },
  ): Promise<NoteDto> {
    const created = await req<NoteDto>(apiUrl(`vorgang/${vorgangId}/notes`), {
      method: "POST",
      body: JSON.stringify(input),
    });
    invalidateByPrefix("vorgang.");
    return created;
  },
  async update(
    noteId: number,
    input: {
      /** Fehlt = Text unverändert lassen (etwa beim reinen Umsortieren). */
      text?: string;
      mentions?: string[];
      todoId?: number | null;
      riskObjectId?: number | null;
      contractId?: number | null;
      /** Blockfelder (PO 08.09.2026). */
      format?: "text" | "html";
      refs?: NoteRefDto[];
      sortIndex?: number | null;
      /** Gezogene Breite in Pixel; null = mit dem Inhalt wachsen. */
      breite?: number | null;
    },
  ): Promise<NoteDto> {
    const updated = await req<NoteDto>(apiUrl(`vorgang-notes/${noteId}`), {
      method: "PUT",
      body: JSON.stringify(input),
    });
    invalidateByPrefix("vorgang.");
    return updated;
  },
  /**
   * Vollständige Reihenfolge genau eines Vorgang-/To-do-Strangs. Der Server
   * validiert die ID-Menge und schreibt VorgangNote + TodoNote atomar.
   */
  async reorder(
    vorgangId: number,
    todoId: number | null,
    reihenfolge: ReihenfolgePatch[],
  ): Promise<void> {
    await req<{ ok: true }>(apiUrl(`vorgang/${vorgangId}/notiz-reihenfolge`), {
      method: "PUT",
      body: JSON.stringify({ todoId, reihenfolge }),
    });
    invalidateByPrefix("vorgang.");
  },
  async remove(noteId: number): Promise<void> {
    await req<void>(apiUrl(`vorgang-notes/${noteId}`), { method: "DELETE" });
    invalidateByPrefix("vorgang.");
  },
  /**
   * DEN HAKEN AM KOMMENTAR SETZEN ODER ZURÜCKNEHMEN (PO 18.09.2026).
   *
   * Eigener Weg statt eines Feldes in `update`: Das Ändern bleibt dem Autor
   * vorbehalten, das Abhaken steht jedem offen, der den Vorgang sieht
   * (PO 20.09.2026: „erstmal jeder"). Abhaken ist keine Bearbeitung — es
   * hinterlässt weder `updatedAt` noch einen Eintrag in `editors`.
   */
  async setErledigt(noteId: number, erledigt: boolean): Promise<NoteDto> {
    const updated = await req<NoteDto>(apiUrl(`vorgang-notes/${noteId}/erledigt`), {
      method: "PUT",
      body: JSON.stringify({ erledigt }),
    });
    invalidateByPrefix("vorgang.");
    return updated;
  },
  /** T4-PR3: Notiz `sourceId` in `targetId` zusammenführen (Quelle wird archiviert). */
  async merge(sourceId: number, targetId: number): Promise<void> {
    await req<{ ok: true }>(apiUrl(`vorgang-notes/${sourceId}/merge`), {
      method: "POST",
      body: JSON.stringify({ targetId }),
    });
    invalidateByPrefix("vorgang.");
  },
};
